import { useState, useRef, useEffect } from 'react';
import { X, Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { BOAMP_DESCRIPTORS } from '../../constants/boamp-descriptors';

interface DescriptorMultiSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  maxDisplayed?: number;
}

export function DescriptorMultiSelect({
  value,
  onChange,
  placeholder = 'Rechercher des mots-clés...',
  maxDisplayed = 3,
}: DescriptorMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Filtrer les descripteurs selon la recherche
  const filteredDescriptors = BOAMP_DESCRIPTORS.filter((descriptor) =>
    descriptor.toLowerCase().includes(search.toLowerCase())
  );

  const toggleDescriptor = (descriptor: string) => {
    if (value.includes(descriptor)) {
      onChange(value.filter((v) => v !== descriptor));
    } else {
      onChange([...value, descriptor]);
    }
  };

  const removeDescriptor = (descriptor: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter((v) => v !== descriptor));
  };

  const clearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  };

  // Focus sur l'input quand le popover s'ouvre
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between h-auto min-h-10 px-3 py-2"
        >
          <div className="flex flex-wrap gap-1 flex-1">
            {value.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : (
              <>
                {value.slice(0, maxDisplayed).map((descriptor) => (
                  <Badge
                    key={descriptor}
                    variant="secondary"
                    className="mr-1 mb-1"
                  >
                    {descriptor}
                    <button
                      type="button"
                      className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                      onClick={(e) => removeDescriptor(descriptor, e)}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
                {value.length > maxDisplayed && (
                  <Badge variant="outline" className="mr-1 mb-1">
                    +{value.length - maxDisplayed}
                  </Badge>
                )}
              </>
            )}
          </div>
          <div className="flex items-center gap-1 ml-2">
            {value.length > 0 && (
              <button
                type="button"
                className="p-1 hover:bg-muted rounded"
                onClick={clearAll}
              >
                <X className="h-4 w-4 text-muted-foreground" />
              </button>
            )}
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <div className="p-2 border-b">
          <Input
            ref={inputRef}
            placeholder="Rechercher un mot-clé..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9"
          />
        </div>
        <div className="max-h-[300px] overflow-y-auto p-1">
          {filteredDescriptors.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Aucun mot-clé trouvé
            </div>
          ) : (
            filteredDescriptors.map((descriptor) => {
              const isSelected = value.includes(descriptor);
              return (
                <button
                  key={descriptor}
                  type="button"
                  onClick={() => toggleDescriptor(descriptor)}
                  className={cn(
                    'w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm cursor-pointer',
                    'hover:bg-accent hover:text-accent-foreground',
                    isSelected && 'bg-accent'
                  )}
                >
                  <div
                    className={cn(
                      'flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                      isSelected
                        ? 'bg-primary text-primary-foreground'
                        : 'opacity-50'
                    )}
                  >
                    {isSelected && <Check className="h-3 w-3" />}
                  </div>
                  <span className="flex-1 text-left">{descriptor}</span>
                </button>
              );
            })
          )}
        </div>
        {value.length > 0 && (
          <div className="p-2 border-t text-xs text-muted-foreground">
            {value.length} mot{value.length > 1 ? 's' : ''}-clé
            {value.length > 1 ? 's' : ''} sélectionné
            {value.length > 1 ? 's' : ''}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
